import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import { lazy, Suspense } from "react";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import { Spinner } from "@/components/ui/spinner";

// Lazy-loaded pages
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Book = lazy(() => import("./pages/Book"));
const Quote = lazy(() => import("./pages/Quote"));
const Commercial = lazy(() => import("./pages/Commercial"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Account = lazy(() => import("./pages/Account"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const Locations = lazy(() => import("./pages/Locations"));
const LocationDetail = lazy(() => import("./pages/LocationDetail"));
const Gallery = lazy(() => import("./pages/Gallery"));
const PestGuide = lazy(() => import("./pages/PestGuide"));
const SpinWheel = lazy(() => import("./pages/SpinWheel"));
const PestCalculator = lazy(() => import("./pages/PestCalculator"));
const PestCalendar = lazy(() => import("./pages/PestCalendar"));
const PestIdentifier = lazy(() => import("./pages/PestIdentifier"));
const FreeAdvice = lazy(() => import("./pages/FreeAdvice"));
const BookingPaymentSuccess = lazy(() => import("./pages/BookingPaymentSuccess"));
const Romana = lazy(() => import("./pages/Romana"));
const FAQ = lazy(() => import("./pages/FAQ"));
const NearMe = lazy(() => import("./pages/NearMe"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Spinner className="w-8 h-8 text-[oklch(0.22_0.06_255)]" />
    </div>
  );
}

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/services" component={Services} />
        <Route path="/services/:slug" component={ServiceDetail} />
        <Route path="/book" component={Book} />
        <Route path="/quote" component={Quote} />
        <Route path="/commercial" component={Commercial} />
        <Route path="/contact" component={Contact} />
        <Route path="/about" component={About} />
        <Route path="/account" component={Account} />
        <Route path="/admin" component={AdminDashboard} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/terms" component={Terms} />
        <Route path="/blog" component={Blog} />
        <Route path="/blog/:slug" component={BlogArticle} />
        <Route path="/locations" component={Locations} />
        <Route path="/locations/:slug" component={LocationDetail} />
        <Route path="/gallery" component={Gallery} />
        <Route path="/pest-guide" component={PestGuide} />
        <Route path="/spin" component={SpinWheel} />
        <Route path="/calculator" component={PestCalculator} />
        <Route path="/pest-calendar" component={PestCalendar} />
        <Route path="/identify" component={PestIdentifier} />
        <Route path="/free-advice" component={FreeAdvice} />
        <Route path="/booking-payment-success" component={BookingPaymentSuccess} />
        <Route path="/romana" component={Romana} />
        <Route path="/faq" component={FAQ} />
        <Route path="/pest-control-near-me" component={NearMe} />
        <Route path="/404" component={NotFound} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          <Router />
          <PWAInstallPrompt />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
